import { useState } from 'react';
import { Link } from 'react-router-dom';

const FAQ_ITEMS = [
  {
    id: 'add-baby',
    question: 'How do I add a new baby profile?',
    answer: 'From the Dashboard, tap the "Add" button. You\'ll be asked what you\'d like to track — choose "Baby" for a newborn/infant profile, or "Pregnancy" to start tracking your pregnancy journey. For a baby profile, enter the name, date of birth, and gender. All information is encrypted on your device before being stored — the server never sees your baby\'s details.',
  },
  {
    id: 'caregivers',
    question: 'Can multiple caregivers track the same baby?',
    answer: 'Yes! Go to your baby\'s profile and tap "Invite Caregiver" to generate a secure invite link. Share this link with your partner, family member, or nanny. Once they accept, all events sync in real time across everyone\'s devices.',
  },
  {
    id: 'encryption',
    question: 'What does "zero-knowledge encryption" mean?',
    answer: 'It means the server never sees your readable data. Everything — baby names, feeding logs, notes — is encrypted on your device using your password before being sent to the server. Even we can\'t read your data. Only you and people you explicitly share with can decrypt it.',
  },
  {
    id: 'timers',
    question: 'How do I use timers?',
    answer: 'On the Dashboard, tap the "Feed" or "Sleep" button to start a timer. You can pause and resume at any time. For breastfeeding, switch sides with one tap. When you stop the timer, it automatically fills in the event form with the recorded duration.',
  },
  {
    id: 'offline',
    question: 'Can I use myTotTot offline?',
    answer: 'Yes! myTotTot is a Progressive Web App (PWA) that works offline. Any events you log while offline are queued and automatically synced when your internet connection returns. You\'ll see a "pending sync" indicator showing how many events are waiting.',
  },
  {
    id: 'install',
    question: 'How do I install myTotTot on my phone?',
    answer: 'On iOS: Open Safari, tap the Share button, then "Add to Home Screen". On Android: Open Chrome, tap the three-dot menu, then "Install app" or "Add to Home Screen". The app will appear on your home screen just like a native app.',
  },
  {
    id: 'export',
    question: 'How do I export my baby\'s data?',
    answer: 'Go to Settings and tap "Export Data". You can export as PDF or CSV. Choose a date range and which event types to include. All data is decrypted on your device during export — nothing unencrypted ever touches the server.',
  },
  {
    id: 'emergency',
    question: 'What is Emergency Access?',
    answer: 'Emergency Access lets you designate trusted contacts (like grandparents) who can request time-limited access to your baby\'s health information. When they request access, you\'ll be notified and can approve or deny it. Access automatically expires after the time limit you set.',
  },
  {
    id: 'pediatrician',
    question: 'How do I share data with my pediatrician?',
    answer: 'Go to your baby\'s profile and tap "Share Link". You can create a read-only link with specific event types and date ranges. The link is time-limited and can be revoked at any time. Your pediatrician doesn\'t need an account to view the shared data.',
  },
  {
    id: 'reminders',
    question: 'Can I set reminders?',
    answer: 'Yes! Go to your baby\'s Reminders page and tap "New Reminder". You can set one-time or recurring reminders (daily, weekly, monthly). Reminders trigger push notifications so you never miss a feeding, medication, or appointment.',
  },
  {
    id: 'notifications',
    question: 'How do I enable push notifications?',
    answer: 'Go to Settings and tap "Enable Push Notifications". Your browser will ask for permission — tap "Allow". Once enabled, you\'ll receive notifications for reminders, emergency access requests, and caregiver invitations.',
  },
  {
    id: 'event-types',
    question: 'What event types can I track?',
    answer: 'myTotTot supports baby events: Feed (breastfeeding), Bottle (formula/expressed milk), Nappy (diaper changes), Sleep, Spit Up, Medication, Weight, Temperature, Tummy Time, Pump (breast milk), EC (Elimination Communication), and Journal entries. For pregnancy tracking, we also support: Symptom tracking (24 symptom types with severity), Appointments, Blood Pressure, Ultrasound scans, Kick Counts, and Contraction timing.',
  },
  {
    id: 'pregnancy-tracking',
    question: 'Can I track my pregnancy with myTotTot?',
    answer: 'Yes! myTotTot now supports pregnancy tracking. When adding a new profile, choose "Pregnancy" to get started. You\'ll set your due date and pre-pregnancy weight, and can track symptoms (with severity), appointments, blood pressure, ultrasound scans, kick counts, and contraction timing. A weekly gestation tracker with progress bar and a timeline view help you see your entire pregnancy journey at a glance.',
  },
  {
    id: 'pregnancy-conversion',
    question: 'What happens when my baby is born? Can I convert my pregnancy profile?',
    answer: 'Absolutely! myTotTot offers a seamless one-tap conversion from pregnancy to baby profile. When your baby arrives, simply go to your pregnancy profile settings and tap "Convert to Baby Profile". Enter your baby\'s name and gender, and your pregnancy history is preserved while you continue tracking feeds, sleep, nappies, and everything else — all in the same app. No data export, no new account, no switching apps. Your journey continues uninterrupted.',
  },
  {
    id: 'delete-account',
    question: 'How do I delete my account?',
    answer: 'Go to Settings, scroll to the bottom, and tap "Delete Account". You\'ll need to confirm with your password. This permanently deletes all your data, including all babies, events, and photos. This action cannot be undone.',
  },
  {
    id: 'dark-mode',
    question: 'Is there a dark mode?',
    answer: 'Yes! Tap the sun/moon icon in the header to toggle between light and dark mode. Your preference is saved and persists across sessions.',
  },
  {
    id: 'health-metrics',
    question: 'Where can I see feeding patterns and growth charts?',
    answer: 'Tap the "Health Metrics →" link at the bottom of the Dashboard. You\'ll see growth charts with WHO percentile curves, and feeding pattern charts showing when and how much your baby feeds. You can toggle between 7, 14, or 30 day views. Breastfeeds and formula/bottle feeds are colour-coded so you can see the mix at a glance.',
  },
  {
    id: 'upright-timer',
    question: 'What is the post-feed upright timer?',
    answer: 'It\'s a countdown timer that prompts you to keep your baby upright after a feed (to help prevent spit-up). To set it up, go to your baby\'s profile, scroll to the "Post-Feed Upright Timer" section, and enter how many minutes you want. After that, every time you log a feed, you\'ll be asked if you want to start the countdown.',
  },
  {
    id: 'support-tickets',
    question: 'How do I report a problem or request a feature?',
    answer: 'Go to Help & Support and tap "Raise a Ticket". Choose whether it\'s a problem or feature request, describe the issue, and optionally attach screenshots. Our team will respond as soon as possible. You can track the status of your tickets and reply directly from the ticket view.',
  },
  {
    id: 'tummy-time',
    question: 'How does the tummy time timer work?',
    answer: 'Tap the 🏋️ Tummy timer button on the Dashboard to start a dedicated tummy time session. The timer shows a dual display: total tummy time and head-up time. Tap "Head Up" when your baby lifts their head, and "Head Down" when they rest — this tracks how long they hold their head up during the session. When you stop the timer, it opens the event form with both durations pre-filled. Tummy time uses MM:SS format since sessions are typically short. You can also enter durations manually using the touch-friendly MM:SS picker.',
  },
  {
    id: 'ec-tracking',
    question: 'What is EC (Elimination Communication) tracking?',
    answer: 'EC tracking lets you log Elimination Communication sessions — holding your baby over a potty or toilet instead of using nappies. For each session, you can record pee and poop outcomes with three levels: "none", "a little", or "loads". View patterns over time in Health Metrics with a stacked bar chart showing daily outcomes. The chart breaks down pee and poop catches by quantity, helping you spot patterns in your baby\'s elimination routine. Toggle between 7, 14, or 30 day views.',
  },
  {
    id: 'health-metrics-full',
    question: 'What charts are available in Health Metrics?',
    answer: 'Health Metrics includes several interactive charts depending on your profile type. For baby profiles: Growth Charts (weight, height, head circumference with WHO percentile curves), Feeding Patterns (scatter plot of feed times and bar chart of average volume by hour), Medication Frequency (scatter plot and stacked bar chart), EC Patterns (daily stacked bar chart of elimination outcomes), and Tummy Time progress (line chart with total and head-up time per session). For pregnancy profiles: Weight Gain chart, Blood Pressure trends, and Kick Count patterns. All charts support 7-day, 14-day, and 30-day toggles and work in dark mode.',
  },
  {
    id: 'pregnancy-events',
    question: 'How do pregnancy event types work?',
    answer: 'Pregnancy tracking includes six event types. Symptom: choose from 24 symptom types (nausea, back pain, heartburn, etc.) with mild/moderate/severe severity. Kick Count: log fetal movement sessions with a count of kicks felt — the Health Metrics page charts your kick count patterns over time. Contraction: time and log contractions with duration and frequency as labour approaches. Blood Pressure: record systolic and diastolic readings to monitor trends. Ultrasound: document scan dates with notes and findings. Appointment: log checkups, blood tests, or other prenatal visits with provider details.',
  },
  {
    id: 'pregnancy-health',
    question: 'Does Health Metrics work for pregnancy profiles?',
    answer: 'Yes! Pregnancy profiles have their own Health Metrics page with pregnancy-specific charts: a weight gain chart tracking your weight throughout pregnancy, a blood pressure chart showing systolic and diastolic trends, and a kick count chart displaying fetal movement patterns. All charts support the same 7, 14, and 30 day period toggles and work in dark mode. These charts are great for sharing with your midwife or doctor during prenatal checkups.',
  },
  {
    id: 'sync-issues',
    question: 'My data seems out of sync. What should I do?',
    answer: 'Try a hard refresh: press Ctrl+Shift+R (Windows/Linux) or Cmd+Shift+R (Mac). This clears the cache and reloads the latest data. Also check your internet connection — if you see the offline indicator, events will sync once you\'re back online.',
  },
];

export default function SupportHub() {
  const [openFaqId, setOpenFaqId] = useState<string | null>(null);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Help & Support
      </h1>
      <p className="text-gray-600 dark:text-gray-400 mb-8">
        Find answers in our documentation, or reach out to our support team.
      </p>

      {/* Two large cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Link
          to="/app/docs"
          className="card p-6 hover:shadow-lg transition-shadow group"
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Documentation
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Comprehensive guides for every feature. Learn how to get the most out of myTotTot.
            </p>
          </div>
        </Link>

        <Link
          to="/app/support/tickets"
          className="card p-6 hover:shadow-lg transition-shadow group"
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <svg className="w-8 h-8 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Raise a Ticket
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Report a problem or request a feature. Our team will respond as soon as possible.
            </p>
          </div>
        </Link>
      </div>

      {/* FAQ Accordion */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Frequently Asked Questions
        </h2>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {FAQ_ITEMS.map((item) => (
            <div key={item.id}>
              <button
                onClick={() => setOpenFaqId(openFaqId === item.id ? null : item.id)}
                className="w-full flex items-center justify-between py-4 text-left"
              >
                <span className="text-sm font-medium text-gray-900 dark:text-white pr-4">
                  {item.question}
                </span>
                <svg
                  className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${
                    openFaqId === item.id ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openFaqId === item.id && (
                <div className="pb-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
